import {UserUITheme} from '../user'
import {Hub, HubJSON} from './hub'

export const groupColors = [
  '#b3b3b3',
  '#b31d1d',
  '#e0a51e',
  '#529715',
  '#07bba7',
  '#19577b',
  '#7f30a9'
]

export interface ColumnColor {
  hue: number
}

export interface ColumnColors {
  [hue: number]: string
}

// Same for pastel&acid rows
const columnBgColorsLight: ColumnColors = {
  1: '#E3E4EE',
  2: '#ECE0E0',
  3: '#ECE7E0',
  4: '#ECECE0',
  5: '#E0ECE0',
  6: '#E0ECEB',
  7: '#E0E8EC',
  8: '#E7E0EC',
  9: '#E3E4EE',
  10: '#ECE0E0',
  11: '#ECE7E0',
  12: '#ECECE0',
  13: '#E0ECE0',
  14: '#E0ECEB',
  15: '#E0E8EC',
  16: '#E7E0EC'
}

const columnBgColorsDark: ColumnColors = {
  1: '#252B35',
  2: '#362826',
  3: '#363026',
  4: '#363626',
  5: '#293626',
  6: '#263631',
  7: '#23303C',
  8: '#312636',
  9: '#252B35',
  10: '#362826',
  11: '#363026',
  12: '#363626',
  13: '#293626',
  14: '#263631',
  15: '#23303C',
  16: '#312636'
}

const columnColorsLightOld: ColumnColors = {
  1: '#D7D7D7',
  2: '#EBA3A1',
  3: '#E4C38F',
  4: '#E4DEA0',
  5: '#B2D995',
  6: '#A9D5D5',
  7: '#9AB5DF',
  8: '#C6A5CF',
  9: '#9A9999',
  10: '#B64F4F',
  11: '#E58F14',
  12: '#EAC405',
  13: '#63A441',
  14: '#3DADAD',
  15: '#7095CD',
  16: '#AD68AE'
}

const columnColorsDarkOld: ColumnColors = {
  1: '#5B5A5A',
  2: '#773634',
  3: '#884D17',
  4: '#7c7108',
  5: '#48800d',
  6: '#117e7e',
  7: '#254e8b',
  8: '#632b7a',
  9: '#363636',
  10: '#451210',
  11: '#432604',
  12: '#444501',
  13: '#1c3d03',
  14: '#01383c',
  15: '#02284b',
  16: '#2e0434'
}

const columnColorsLight: ColumnColors = {
  1: '#7B869E',
  2: '#FF8C8C',
  3: '#E9A24F',
  4: '#FCE258',
  5: '#7CAE5E',
  6: '#49C5BC',
  7: '#8CACFF',
  8: '#CC8CFF',
  9: '#667085',
  10: '#EB3737',
  11: '#F2732B',
  12: '#F5CC00',
  13: '#5CDC11',
  14: '#08A7A9',
  15: '#5089F2',
  16: '#E25EF2'
}

const columnColorsDark: ColumnColors = {
  1: '#96A0B6',
  2: '#DA7777',
  3: '#C2945E',
  4: '#FCE258',
  5: '#6A9750',
  6: '#54A09B',
  7: '#6B86CC',
  8: '#AD71DD',
  9: '#667085',
  10: '#DB3C3C',
  11: '#D86727',
  12: '#D4B102',
  13: '#58B522',
  14: '#08A7A9',
  15: '#467DE1',
  16: '#BF48CD'
}

const allowedColorsHueForAutoMatch = [1, 3, 4, 6, 7, 8, 9, 11, 12, 14, 15, 16]

export const isColorDark = (color: ColumnColor): boolean => {
  if (color.hue === 12) {
    return false
  }
  return color.hue > 8
}

export const getColumnColorNumber = (
  column: Pick<HubJSON, 'data' | 'description'>,
  columns?: (HubJSON | Hub)[]
): ColumnColor => {
  if (columns) {
    const ix = columns.length % allowedColorsHueForAutoMatch.length
    return {
      hue: allowedColorsHueForAutoMatch[ix]
    }
  }
  return getColumnColorNumberFromData(column)
}

export const getColumnColorNumberFromData = (
  column: Pick<HubJSON, 'data' | 'description'>
): ColumnColor => {
  const color = {
    hue: 1
  }
  if (column.data?.columnColor?.hue) {
    return column.data.columnColor
  }
  if (column.description?.length) {
    switch (column.description) {
      case groupColors[0]:
        return {
          hue: 1
        }
      case groupColors[1]:
        return {
          hue: 2
        }
      case groupColors[2]:
        return {
          hue: 3
        }
      case groupColors[3]:
        return {
          hue: 5
        }
      case groupColors[4]:
        return {
          hue: 6
        }
      case groupColors[5]:
        return {
          hue: 7
        }
      case groupColors[6]:
        return {
          hue: 8
        }
      default:
        return color
    }
  }

  return color
}

export const getColumnColorHex = (
  color: ColumnColor,
  theme: UserUITheme,
  // for a transition period keep old colors for gantt&calendar
  isOld = false
): string => {
  if (isOld) {
    if (theme === 'dark') {
      return columnColorsDarkOld[color.hue]
    }
    return columnColorsLightOld[color.hue]
  }
  if (theme === 'dark') {
    return columnColorsDark[color.hue]
  }
  return columnColorsLight[color.hue]
}

export const getColumnBgColorHex = (
  color: ColumnColor,
  theme: UserUITheme
): string => {
  if (theme === 'dark') {
    return columnBgColorsDark[color.hue]
  }
  return columnBgColorsLight[color.hue]
}

export const getColumnColors = (theme: UserUITheme): ColumnColors => {
  if (theme === 'dark') {
    return columnColorsDark
  }
  return columnColorsLight
}
